import { useMemo, useState } from 'react';
import { Combobox, Option, Field } from '@fluentui/react-components';
import { Delete16Regular } from '@fluentui/react-icons';
import { useT } from '../../i18n';

export interface CatalogOption {
  id: string;
  label: string;
}

export interface CatalogSelectProps {
  label?: string;
  options: CatalogOption[];
  /** id elegido; '' = sin asignar. */
  value: string;
  onChange: (id: string) => void;
  /** Texto de la opción "sin asignar" (ej. "Sin cuadrilla"). Si se omite, no se ofrece. */
  emptyLabel?: string;
  /**
   * Alta al momento: si se pasa, al teclear un nombre que no existe aparece "Crear «nombre»".
   * Debe devolver el id del elemento nuevo (el selector lo deja elegido).
   */
  onCreate?: (name: string) => Promise<string> | string;
  /** Borrado desde la lista: muestra una papelera por opción. Debe lanzar o gestionar el error por su cuenta. */
  onDelete?: (id: string) => Promise<void> | void;
  /** Texto de confirmación antes de borrar (si se omite, borra directo). */
  deleteConfirm?: (label: string) => string;
  deleteLabel?: string;
  /** Tamaño del control (por defecto el normal). */
  size?: 'small' | 'medium';
  disabled?: boolean;
  ariaLabel?: string;
}

const CREATE_PREFIX = '__create__:';

/**
 * Selector estándar de catálogos (cuadrillas, categorías, tipos…): lista buscable,
 * opción "sin asignar" y alta al momento. Reemplaza al `Select` nativo + cuadro aparte.
 */
export function CatalogSelect({
  label, options, value, onChange, emptyLabel, onCreate, onDelete, deleteConfirm, deleteLabel, size, disabled, ariaLabel,
}: CatalogSelectProps) {
  const t = useT();
  const selected = options.find(o => o.id === value);
  const [query, setQuery] = useState<string | null>(null);
  const text = query ?? selected?.label ?? (value === '' ? emptyLabel ?? '' : '');

  const q = (query ?? '').trim();
  const filtered = useMemo(() => {
    const lower = q.toLowerCase();
    return !lower || lower === selected?.label.toLowerCase()
      ? options
      : options.filter(o => o.label.toLowerCase().includes(lower));
  }, [options, q, selected]);
  const canCreate = !!onCreate && q.length >= 2 && !options.some(o => o.label.toLowerCase() === q.toLowerCase());

  const combobox = (
    <Combobox
      freeform
      size={size}
      aria-label={ariaLabel ?? label}
      value={text}
      selectedOptions={[value]}
      disabled={disabled}
      onChange={(e) => { setQuery((e.target as HTMLInputElement).value); }}
      // Con "sin asignar" mostrado como texto, al enfocar se limpia para poder escribir un nombre nuevo.
      onFocus={() => { if (!selected && query === null) setQuery(''); }}
      onOpenChange={(_, d) => { if (!d.open) setQuery(null); }}
      onOptionSelect={async (_, d) => {
        // Con `freeform`, Fluent también avisa al teclear (sin opción): se ignora.
        if (d.optionValue === undefined) return;
        const v = d.optionValue as string;
        setQuery(null);
        if (v.startsWith(CREATE_PREFIX)) onChange(await onCreate!(v.slice(CREATE_PREFIX.length)));
        else onChange(v);
      }}
    >
      {emptyLabel !== undefined && !q && <Option value="" text={emptyLabel}>{emptyLabel}</Option>}
      {filtered.map(o => (
        <Option key={o.id} value={o.id} text={o.label}>
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <span>{o.label}</span>
            {onDelete && (
              <span
                role="button" tabIndex={-1} style={{ display: 'inline-flex', cursor: 'pointer', padding: 2 }}
                aria-label={`${deleteLabel ?? t.common.delete} ${o.label}`}
                onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
                onClick={async (e: React.MouseEvent) => {
                  e.stopPropagation();
                  if (deleteConfirm && !window.confirm(deleteConfirm(o.label))) return;
                  await onDelete(o.id);
                  if (o.id === value) onChange('');
                }}
              ><Delete16Regular /></span>
            )}
          </span>
        </Option>
      ))}
      {canCreate && <Option value={CREATE_PREFIX + q} text={q}>{t.common.createNamed.replace('{name}', q)}</Option>}
      {filtered.length === 0 && !canCreate && <Option value="__empty" disabled>{t.common.searchNoResults}</Option>}
    </Combobox>
  );
  return label ? <Field label={label}>{combobox}</Field> : combobox;
}
